import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

/**
 * Multi-tenant context reducer
 * Tracks the current store context (storeId, storeType) to ensure data isolation
 * across different stores in the multi-tenant panel.
 * 
 * This slice should be updated whenever the user switches stores.
 * Middleware watches this slice to detect store changes and clear store-specific state.
 */

export interface TenantState {
  // Current active store identifier
  currentStoreId: string | null;
  
  // Store type (e.g., 'wholesale', 'retail', 'partner')
  currentStoreType: string | null;
  
  // Previous store values (for detecting store switches)
  previousStoreId: string | null;
  previousStoreType: string | null;
  
  // Flag indicating if we're in a store context
  isStoreSelected: boolean;
}

const initialState: TenantState = {
  currentStoreId: null,
  currentStoreType: null,
  previousStoreId: null,
  previousStoreType: null,
  isStoreSelected: false,
};

const tenantSlice = createSlice({
  name: "tenant",
  initialState,
  reducers: {
    /**
     * Set the current store context.
     * Middleware will detect this change and clear store-specific state if store changed.
     */
    setStoreContext: (
      state,
      action: PayloadAction<{ storeId: string; storeType: string }>,
    ) => {
      // Save previous values for comparison
      state.previousStoreId = state.currentStoreId;
      state.previousStoreType = state.currentStoreType;
      
      // Update current values
      state.currentStoreId = action.payload.storeId;
      state.currentStoreType = action.payload.storeType;
      state.isStoreSelected = true;
    },

    /**
     * Clear the current store context (e.g., on logout)
     */
    clearStoreContext: (state) => {
      state.previousStoreId = state.currentStoreId;
      state.previousStoreType = state.currentStoreType;
      state.currentStoreId = null;
      state.currentStoreType = null;
      state.isStoreSelected = false;
    },

    /**
     * Reset the previous store values after state clearing
     * (Called by middleware after clearing store-specific state)
     */
    resetPreviousStoreContext: (state) => {
      state.previousStoreId = null;
      state.previousStoreType = null;
    },
  },
  extraReducers: (builder) => {
    /**
     * When store-specific state is reset (user switched stores),
     * also clear the previous store context markers
     */
    builder.addCase(resetStoreSpecificState, (state) => {
      state.previousStoreId = null;
      state.previousStoreType = null;
    });
  },
});

export const { setStoreContext, clearStoreContext, resetPreviousStoreContext } =
  tenantSlice.actions;

export default tenantSlice.reducer;
